import { tr } from "date-fns/locale";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft } from "lucide-react";
import * as leaveAction from "../Store/_redux/LeaveStore/leaveAction";
import { setLoginUser } from "../Store/_redux/AuthStore/authSlice";
import { useTranslation } from "react-i18next";
import { LEAVE_TYPES, LeaveTypeEnum, leaveTypeLabel } from "../modal/leave-type";

const initialFormValues = {
  tc: "",
  startDate: "",
  finishDate: "",
  totalDate: "",
  comment: "",
  leavesType: "",
};

function Form({ onCancel, onSuccess }) {
  const { t } = useTranslation();
  const [selectedType, setSelectedType] = useState("");
  const [isStartHalf, setIsStartHalf] = useState(false);
  const [isEndHalf, setIsEndHalf] = useState(false);
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [form, setForm] = useState(initialFormValues);
  const [attachment, setAttachment] = useState(null);
  const fileInputRef = useRef(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth.loginUser);

  const handleTypeSelect = (event) => setSelectedType(event.target.value);

  const calculateTotalDays = (start, end, isStartHalf, isEndHalf) => {
    if (!start || !end) return 0;
    let current = moment(start);
    const finishDate = moment(end);
    let count = 0;
    while (current <= finishDate) {
      const day = current.day();
      if (day !== 0 && day !== 6) count++;
      current.add(1, "day");
    }
    if (isStartHalf) count -= 0.5;
    if (isEndHalf) count -= 0.5;
    return Math.max(count, 0);
  };

  const handleSelect = (range) => {
    setRange(range);
    const total = calculateTotalDays(range?.from, range?.to, isStartHalf, isEndHalf);
    setForm((prev) => ({
      ...prev,
      startDate: range?.from ? moment(range.from).format("YYYY-MM-DD") : "",
      finishDate: range?.to ? moment(range.to).format("YYYY-MM-DD") : "",
      totalDate: total,
    }));
  };

  const handleStartHalfChange = (e) => {
    const checked = e.target.checked;
    setIsStartHalf(checked);
    if (range.from && range.to) {
      const total = calculateTotalDays(range.from, range.to, checked, isEndHalf);
      setForm((prev) => ({ ...prev, totalDate: total }));
    }
  };

  const handleEndHalfChange = (e) => {
    const checked = e.target.checked;
    setIsEndHalf(checked);
    if (range.from && range.to) {
      const total = calculateTotalDays(range.from, range.to, isStartHalf, checked);
      setForm((prev) => ({ ...prev, totalDate: total }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const capitalizedValue =
      typeof value === "string" && name !== "tc"
        ? value.charAt(0).toUpperCase() + value.slice(1)
        : value;
    setForm((prev) => ({ ...prev, [name]: capitalizedValue }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Dosya boyutu en fazla 20MB olabilir.");
      return;
    }
    setAttachment(file);
    toast.success(`${file.name} seçildi`);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const start = new Date(form.startDate);
    const end = new Date(form.finishDate);

    if (!form.startDate || !form.finishDate) {
      toast.error("Lütfen izin başlangıç ve bitiş tarihlerini seçiniz.");
      return;
    }

    if (!selectedType) {
      toast.error("Lütfen izin türü seçiniz.");
      return;
    }

    if (end < start) {
      toast.error("Bitiş tarihi, başlangıç tarihinden önce olamaz!");
      return;
    }

    if (selectedType === LeaveTypeEnum.ANNUAL) {
      const remainingLeaveDays = auth.remainingLeaveDays ?? 0;
      const totalDays = form.totalDate;
      if (totalDays > remainingLeaveDays + 6) {
        toast.warning(
          `Yıllık izin hakkınız ${remainingLeaveDays} gün. Seçilen izin gün sayısı ${totalDays} bu sınırların dışındadır.`
        );
        return;
      }
    }

    const formData = new FormData();
    const payload = { ...form, leavesType: selectedType, isStartHalf, isEndHalf };
    Object.entries(payload).forEach(([key, value]) => formData.append(key, value));
    if (attachment) {
      formData.append("attachment", attachment);
    }

    try {
      const res = await dispatch(leaveAction.createLeave(formData));

      if (res.status === 201) {
        toast.success(res.data?.message || "İzin başvurusu başarıyla gönderildi 🎉", {
          position: "top-right",
          autoClose: 3000,
          theme: "colored",
        });

        if (res.data?.updatedUser) {
          dispatch(setLoginUser(res.data.updatedUser));
        }

        await dispatch(leaveAction.getLeave(auth));

        if (onSuccess) {
          onSuccess();
        } else {
          setTimeout(() => navigate("/izinler"), 3000);
        }
        return;
      }

      if (res.status >= 400) {
        toast.error(res.data?.message || "Bir hata oluştu. Lütfen tekrar deneyin.", {
          position: "top-right",
          autoClose: 3000,
          theme: "colored",
        });
        return;
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error.message ||
        "Sunucu hatası. Lütfen tekrar deneyin.";
      toast.error(msg, {
        position: "top-right",
        autoClose: 3000,
        theme: "colored",
      });
    }
  };

  useEffect(() => {
    if (auth?.tc) setForm((prev) => ({ ...prev, tc: auth.tc }));
  }, [auth?.tc]);

  return (
    <form
      onSubmit={onSubmit}
      className={`flex flex-col justify-center mx-auto ${onCancel ? 'w-full py-0' : 'w-[600px] py-[30px]'}`}
    >
      {!onCancel && (
        <button
          type="button"
          onClick={() => navigate("/izinler")}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-medium text-sm mb-2 self-start"
        >
          <ArrowLeft size={16} />
          {t("createLeave.backToLeaves")}
        </button>
      )}

      <h1 className={`font-semibold ${onCancel ? 'text-xl py-2' : 'text-3xl py-5'}`}>İzin Formu</h1>



      {/* Takvim */}
      <div className="flex flex-col mb-5">
        <label className="font-semibold py-2">İzin Tarih Aralığı</label>
        <div className="leave-form flex justify-start bg-white p-3 rounded-md shadow-sm">
          <DayPicker
            mode="range"
            captionLayout="dropdown"
            fromYear={2015}
            toYear={2035}
            selected={range}
            onSelect={handleSelect}
            locale={tr}
            className="day-picker"
            modifiers={{
              halfStart: range?.from && isStartHalf ? [range.from] : [],
              halfEnd: range?.to && isEndHalf ? [range.to] : [],
            }}
            modifiersClassNames={{
              halfStart: "half-start",
              halfEnd: "half-end",
            }}
            footer={
              range?.from && range?.to ? (
                <p className="text-sm text-gray-500 mt-2">
                  Seçilen aralık: {moment(range.from).format("DD.MM.YYYY")} -{" "}
                  {moment(range.to).format("DD.MM.YYYY")}
                </p>
              ) : (
                <p className="text-sm text-gray-400 mt-2">
                  Lütfen tarih aralığı seçiniz
                </p>
              )
            }
          />
        </div>
      </div>

      {/* Yarım Gün Seçenekleri */}
      <div className="flex flex-col gap-2 mb-5">
        <label className="font-semibold">Yarım Gün Seçenekleri</label>
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={isStartHalf}
            onChange={handleStartHalfChange}
          />
          <span>Başlangıç günü yarım</span>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={isEndHalf}
            onChange={handleEndHalfChange}
          />
          <span>Bitiş günü yarım</span>
        </div>
      </div>

      {/* Toplam izin günü */}
      <label className="font-semibold py-2">Toplam izin günü</label>
      <input
        name="totalDate"
        value={form.totalDate || 0}
        disabled
        type="number"
        className="border rounded-md px-3 py-2 border-input-b text-gray-500"
      />

      {/* İzin Türü */}
      <label className="font-semibold py-2 mt-4">İzin Türü Seçiniz</label>
      <div className="grid grid-cols-2 gap-x-3 gap-y-2 mb-5">
        {LEAVE_TYPES.map((type) => (
          <div
            key={type}
            className={`flex px-3 gap-x-2 border-[1px] py-2 rounded-md ${
              selectedType === type
                ? "bg-select-bg border-select-b"
                : "bg-input-bg border-transparent"
            }`}
          >
            <input
              type="radio"
              id={type}
              name="leavesType"
              value={type}
              checked={selectedType === type}
              onClick={handleTypeSelect}
              onChange={handleChange}
            />
            <span>{leaveTypeLabel(t, type)}</span>
          </div>
        ))}
      </div>

      {/* Açıklama */}
      {selectedType !== LeaveTypeEnum.ANNUAL && (
        <textarea
          onChange={handleChange}
          id="İzin Sebebi"
          name="comment"
          value={form.comment}
          className="border rounded-md px-3 py-2 border-input-b mb-5"
          placeholder="İzin alma sebebinizi açıklayın"
        />
      )}

      {/* Dosya Yükleme */}
      <div>
        <input
          type="file"
          ref={fileInputRef}
          accept=".pdf,.docx"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 mb-4"
        >
          {attachment ? `📄 ${attachment.name}` : "Rapor Yükle"}
        </button>
      </div>

      {/* Gönder Butonu */}
      <div className="flex justify-end my-4">
        <button
          type="submit"
          className="bg-select-b px-6 py-3 rounded-md text-white hover:opacity-90 transition"
        >
          İzin başvurusunu gönder
        </button>
      </div>
    </form>
  );
}

export default Form;
